import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Paper,
  Skeleton,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import Groups2Icon from "@mui/icons-material/Groups2";
import EventSeatIcon from "@mui/icons-material/EventSeat";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useAuth } from "@workos-inc/authkit-react";
import UsersTable from "./UsersTable";
import AddUserForm from "../../pages/Admin/ManageUsers/AddUserForm";
import BulkUserUpload from "../../pages/Admin/ManageUsers/BulkUserUpload";
import CsvPreview from "../../pages/Admin/ManageUsers/CsvPreview";
import UserStatusDialog from "../../pages/Admin/ManageUsers/UserStatusDialog";
import EditBadgeDialog from "../../pages/Admin/ManageUsers/EditBadgeDialog";
import EditRankDialog from "../../pages/Admin/ManageUsers/EditRankDialog";
import EditRoleDialog from "../../pages/Admin/ManageUsers/EditRoleDialog";
import { useStationUsers } from "../hooks/useStationUsers";
import { CSV_PREVIEW_DEFAULT_PAGINATION } from "../constants/userConstants";
import { NewUser } from "../utils/userValidation";
import { useToast } from "../toast";

export interface StationUsersPanelProps {
  stationId: string;
}

const StationUsersPanel: React.FC<StationUsersPanelProps> = ({
  stationId,
}) => {
  const theme = useTheme();
  const toast = useToast();

  const {
    users,
    seats,
    isSeatsLoading,
    lookupFailures,
    isLoading,
    loadFailed,
    loadErrorMessage,
    searchTerm,
    setSearchTerm,
    resendingInviteEmail,
    resettingPasswordEmail,
    statusUpdatingEmail,
    statusDialogOpen,
    statusAction,
    selectedUser,
    isUpdatingStatus,
    refresh,
    handleResendInvite,
    handleResetPassword,
    handleStatusChangeRequest,
    closeStatusDialog,
    handleStatusConfirm,
    badgeDialogUser,
    isSavingBadge,
    openBadgeDialog,
    closeBadgeDialog,
    handleBadgeSave,
    rankDialogUser,
    isSavingRank,
    openRankDialog,
    closeRankDialog,
    handleRankSave,
    roleDialogUser,
    isSavingRole,
    openRoleDialog,
    closeRoleDialog,
    handleRoleSave,
  } = useStationUsers(stationId);

  // Caller identity for the role-editing gates: the token's role slug and
  // email mirror what the backend checks server-side.
  const { role: callerRoleSlug, user: currentUser } = useAuth();

  const [csvUsers, setCsvUsers] = useState<NewUser[]>([]);
  const [csvPage, setCsvPage] = useState(0);
  const [csvRowsPerPage, setCsvRowsPerPage] = useState(
    CSV_PREVIEW_DEFAULT_PAGINATION
  );

  const handleCsvRowsPerPageChange = (value: number) => {
    setCsvRowsPerPage(value);
    setCsvPage(0);
  };

  const counts = useMemo(() => {
    const total = users.length;
    const active = users.filter(
      (user) => (user.status || "").toUpperCase() === "ACTIVE",
    ).length;
    // Matches UsersTable's pending semantics: pending memberships AND
    // invitation-only rows are both outstanding invites.
    const pending = users.filter((user) =>
      ["PENDING", "INVITED"].includes((user.status || "").toUpperCase()),
    ).length;
    return { total, active, pending };
  }, [users]);

  const seatsAtCapacity = !!seats && seats.used >= seats.limit;

  const statCards = [
    {
      label: "Total Users",
      value: String(counts.total),
      icon: <Groups2Icon fontSize="medium" />,
      accent: theme.palette.primary.main,
      valueColor: undefined as string | undefined,
    },
    {
      label: "Active Accounts",
      value: String(counts.active),
      icon: <PeopleAltIcon fontSize="medium" />,
      accent: theme.palette.success.main,
      valueColor: "success.main" as string | undefined,
    },
    {
      label: "Pending Invites",
      value: String(counts.pending),
      icon: <PersonAddAlt1Icon fontSize="medium" />,
      accent: theme.palette.warning.main,
      valueColor: "warning.main" as string | undefined,
    },
    ...(seats
      ? [
          {
            label: "Seats Used",
            value: `${seats.used} of ${seats.limit}`,
            icon: <EventSeatIcon fontSize="medium" />,
            accent: seatsAtCapacity
              ? theme.palette.error.main
              : theme.palette.info.main,
            valueColor: seatsAtCapacity
              ? ("error.main" as string | undefined)
              : undefined,
          },
        ]
      : []),
  ];
  // Reserve the Seats card's slot while its (slower) query is in flight so
  // the row doesn't reflow from thirds to quarters when it settles.
  const showSeatsSlot = !!seats || isSeatsLoading;
  const statCardSize = showSeatsSlot ? 3 : 4;

  return (
    <Box>
      {loadFailed ? (
        <Paper
          elevation={0}
          sx={{
            p: 5,
            mb: 3,
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.error.main, 0.4)}`,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <ErrorOutlineIcon color="error" sx={{ fontSize: 40 }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Couldn't load the station's users
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ maxWidth: 480, textAlign: "center" }}
          >
            {loadErrorMessage} This is a loading problem — the station's users
            haven't gone anywhere.
          </Typography>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={refresh}
            sx={{ mt: 1, borderRadius: 2, textTransform: "none", fontWeight: 600 }}
          >
            Try again
          </Button>
        </Paper>
      ) : (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {statCards.map((card) => (
              <Grid key={card.label} size={{ xs: 12, sm: statCardSize }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontWeight: 600, mb: 0.5 }}
                    >
                      {card.label}
                    </Typography>
                    <Typography
                      variant="h4"
                      sx={{ fontWeight: 700, color: card.valueColor }}
                    >
                      {card.value}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: alpha(card.accent, 0.1),
                      color: card.accent,
                      display: "flex",
                    }}
                  >
                    {card.icon}
                  </Box>
                </Paper>
              </Grid>
            ))}
            {isSeatsLoading && !seats && (
              <Grid size={{ xs: 12, sm: statCardSize }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                  }}
                >
                  <Skeleton width={90} sx={{ mb: 0.5 }} />
                  <Skeleton width={70} height={42} />
                </Paper>
              </Grid>
            )}
          </Grid>

          {lookupFailures > 0 && (
            <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
              {lookupFailures === 1
                ? "1 user couldn't be loaded"
                : `${lookupFailures} users couldn't be loaded`}{" "}
              from the identity provider, so the list below is incomplete.
              Refresh to try again before re-adding anyone.
            </Alert>
          )}

          <UsersTable
            users={users}
            isLoading={isLoading}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            onRefresh={refresh}
            onResendInvite={handleResendInvite}
            onResetPassword={handleResetPassword}
            onEditBadge={openBadgeDialog}
            onEditRank={openRankDialog}
            onEditRole={openRoleDialog}
            onStatusChange={handleStatusChangeRequest}
            resendingEmail={resendingInviteEmail}
            resettingPasswordEmail={resettingPasswordEmail}
            statusUpdatingEmail={statusUpdatingEmail}
            currentUserEmail={currentUser?.email ?? null}
            callerRoleSlug={callerRoleSlug ?? null}
          />
        </>
      )}

      {/* Suppressed while the roster error card is up — "deactivate a user"
          advice is incongruous next to a list that failed to load. */}
      {!loadFailed && seatsAtCapacity && seats && (
        <Alert severity="warning" sx={{ mt: 2, borderRadius: 2 }}>
          All {seats.limit} of your station's seats are in use (invitations
          that haven't been accepted yet hold a seat). To add someone new,
          deactivate a user to free a seat or contact KARDA to increase your
          limit.
        </Alert>
      )}

      <Grid container spacing={3} alignItems="stretch" sx={{ mt: 3 }}>
        <Grid size={{ xs: 12, md: 6 }} sx={{ minWidth: 0 }}>
          <AddUserForm
            stationId={stationId}
            onUserAdded={refresh}
            onError={toast.error}
            onSuccess={toast.success}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }} sx={{ minWidth: 0 }}>
          <BulkUserUpload
            stationId={stationId}
            onUsersUploaded={refresh}
            onError={toast.error}
            onSuccess={toast.success}
            csvUsers={csvUsers}
            setCsvUsers={setCsvUsers}
            resetPage={() => setCsvPage(0)}
          />
        </Grid>
        {csvUsers.length > 0 && (
          <Grid size={12} sx={{ minWidth: 0 }}>
            <CsvPreview
              csvUsers={csvUsers}
              page={csvPage}
              rowsPerPage={csvRowsPerPage}
              onPageChange={setCsvPage}
              onRowsPerPageChange={handleCsvRowsPerPageChange}
            />
          </Grid>
        )}
      </Grid>

      <UserStatusDialog
        open={statusDialogOpen}
        action={statusAction}
        user={selectedUser}
        isUpdating={isUpdatingStatus}
        onClose={closeStatusDialog}
        onConfirm={handleStatusConfirm}
      />

      <EditBadgeDialog
        open={!!badgeDialogUser}
        user={badgeDialogUser}
        isSaving={isSavingBadge}
        onClose={closeBadgeDialog}
        onSave={handleBadgeSave}
      />

      <EditRankDialog
        open={!!rankDialogUser}
        user={rankDialogUser}
        isSaving={isSavingRank}
        onClose={closeRankDialog}
        onSave={handleRankSave}
      />

      <EditRoleDialog
        open={!!roleDialogUser}
        user={roleDialogUser}
        callerRoleSlug={callerRoleSlug ?? null}
        isSaving={isSavingRole}
        onClose={closeRoleDialog}
        onSave={handleRoleSave}
      />
    </Box>
  );
};

export default StationUsersPanel;
